import React, { useCallback, useMemo } from 'react';
import { Box, Inscription, List } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { EMPTY_FIELD_VALUE, SHORT_DATE_FORMAT } from '@/common/constants';
import routes from '@/common/routes';
import FilePreview from '@/components/FilePreview';
import { Receipt } from '@/modules/employee/Employee/Documents/myDocuments.types';
import { useGeneralInfoStore } from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores';

interface Props extends Receipt {
  onView: (id?: string) => void;
  onDelete: (id?: string) => void;
}

const ReceiptsListItem: React.FC<Props> = ({
  id,
  claimId,
  fileName,
  providerName,
  createdAt,
  fileUrl,
  onView,
  onDelete,
}) => {
  const { employeeId } = useGeneralInfoStore((state) => state.state);
  const date = useMemo(() => {
    if (!createdAt) {
      return EMPTY_FIELD_VALUE;
    }

    return dayjs(createdAt).format(SHORT_DATE_FORMAT);
  }, [createdAt]);

  const getClaimLink = useCallback(
    (claimId?: string) => {
      return routes.TRANSACTION_DETAILS_CLAIM(employeeId, claimId);
    },
    [employeeId]
  );

  const claim = useMemo(() => {
    const formattedLink = getClaimLink(claimId);

    return (
      <a
        href={claimId ? formattedLink : undefined}
        data-testid={`receipt_${id}_claim-link`}
        target="_blank"
        style={{ textDecoration: 'none' }}
        rel="noreferrer"
      >
        <Box direction="row" align="center" style={{ cursor: 'pointer' }}>
          <Inscription color="textAccent" textAlign="center" weight={400} size="medium">
            {providerName || '-'}
          </Inscription>
        </Box>
      </a>
    );
  }, [claimId, getClaimLink, id, providerName]);

  const handleView = useCallback(() => {
    onView(id);
  }, [id, onView]);

  const handleDelete = useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  return (
    <List.Item pad={{ vertical: 'spacing12', horizontal: 'spacing24' }} testId={`receipt-list_item_${id}`}>
      <Box style={{ flex: 0.6 }} justify="center" data-testid={`receipt-list_item_${id}_preview`}>
        <FilePreview fileUrl={fileUrl} testId={`receipt-${id}_file-preview`} />
      </Box>

      <List.Item.Text flex={1.5} testId={`receipt-list_item_${id}_filename`}>
        {fileName}
      </List.Item.Text>

      <List.Item.Text weight={400} flex={1.5} testId={`receipt-list_item_${id}_date`}>
        {date}
      </List.Item.Text>

      <List.Item.Text weight={400} flex={1} testId={`receipt-list_item_${id}_claim`}>
        {claim}
      </List.Item.Text>

      <Box style={{ flex: 0.4 }} align="end">
        <List.Item.Options>
          <List.Item.Options.Item testId={`receipt-list_item_${id}_view-action`} onClick={handleView}>
            View
          </List.Item.Options.Item>

          <List.Item.Options.Item
            testId={`receipt-list_item_${id}_delete-action`}
            color="danger"
            onClick={handleDelete}
          >
            Delete
          </List.Item.Options.Item>
        </List.Item.Options>
      </Box>
    </List.Item>
  );
};

export default ReceiptsListItem;
